import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const feeKeys = {
  all: ['fees'],
  lists: () => [...feeKeys.all, 'list'],
  details: () => [...feeKeys.all, 'detail'],
  detail: (id) => [...feeKeys.details(), id],
};

export const useFees = () => {
  return useQuery({
    queryKey: feeKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('fees');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useFee = (id, options = {}) => {
  return useQuery({
    queryKey: feeKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`fees/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

export const useCreateFee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feeData) => {
      const response = await apiClient.post('fees', feeData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating fee:', error);
    }
  });
};

export const useUpdateFee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`fees/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(feeKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: feeKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating fee:', error);
    }
  });
};

export const useDeleteFee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`fees/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: feeKeys.lists() });
      queryClient.removeQueries({ queryKey: feeKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting fee:', error);
    }
  });
};